import React, { useState } from 'react';
import { Plus, Search, Trash2, Phone, Mail, UserCheck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useDebounce } from '../hooks/useCustom';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { ConfirmModal } from '../components/Modal';
import { Input, Select, FormRow } from '../components/FormComponents';
import Breadcrumb from '../components/Breadcrumb';
import { calculatePercentage, filterByMultipleFields } from '../utils/helpers';

const ROLES = ['Sales Executive', 'Senior Sales', 'Sales Supervisor', 'Sales Manager'];
const BRANCHES = ['Jakarta Pusat', 'Jakarta Selatan', 'Jakarta Barat', 'Jakarta Timur', 'Jakarta Utara', 'Tangerang', 'Bekasi', 'Depok', 'Bogor'];
const STATUSES = [{ value: 'active', label: 'Aktif' }, { value: 'inactive', label: 'Nonaktif' }, { value: 'leave', label: 'Cuti' }];

const emptyForm = { nama: '', role: 'Sales Executive', email: '', noHP: '', cabang: 'Jakarta Pusat', status: 'active', target: 10, pencapaian: 0 };

export default function TeamManagement() {
  const { state, dispatch, showNotification } = useApp();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const debouncedSearch = useDebounce(search, 300);
  const filtered = filterByMultipleFields(state.team, debouncedSearch, ['nama', 'role', 'cabang', 'email']);

  const openAdd = () => { setForm(emptyForm); setEditData(null); setShowModal(true); };
  const openEdit = (m) => { setForm({ ...m }); setEditData(m); setShowModal(true); };

  const handleSubmit = () => {
    if (!form.nama || !form.email) return showNotification('Nama dan email wajib diisi', 'error');
    if (editData) {
      dispatch({ type: 'UPDATE_TEAM_MEMBER', payload: form });
      showNotification('Anggota tim berhasil diperbarui');
    } else {
      dispatch({ type: 'ADD_TEAM_MEMBER', payload: form });
      showNotification('Anggota tim berhasil ditambahkan');
    }
    setShowModal(false);
  };

  const activeCount = state.team.filter(t => t.status === 'active').length;
  const totalAchievement = state.team.reduce((s, t) => s + t.pencapaian, 0);
  const totalTarget = state.team.reduce((s, t) => s + t.target, 0);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Tim Sales' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manajemen Tim Sales</h1>
          <p className="text-sm text-gray-500">{state.team.length} anggota tim</p>
        </div>
        <button onClick={openAdd} className="btn-primary"><Plus size={16}/>Tambah Anggota</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Tim Aktif</p>
          <p className="text-3xl font-bold text-green-600 mt-1">{activeCount}</p>
          <p className="text-xs text-gray-400">dari {state.team.length} total</p>
        </div>
        <div className="card text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Pencapaian</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{totalAchievement}</p>
          <p className="text-xs text-gray-400">target {totalTarget} unit</p>
        </div>
        <div className="card text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Achievement Rate</p>
          <p className="text-3xl font-bold text-blue-600 mt-1">{calculatePercentage(totalAchievement, totalTarget)}%</p>
          <p className="text-xs text-gray-400">tim overall</p>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama, role, cabang..." className="form-input pl-9" />
        </div>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(member => {
          const pct = calculatePercentage(member.pencapaian, member.target);
          return (
            <div key={member.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold text-sm">{member.nama.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{member.nama}</p>
                    <p className="text-xs text-gray-500">{member.role}</p>
                  </div>
                </div>
                <Badge status={member.status} />
              </div>

              <div className="space-y-2 text-xs text-gray-500 mb-3">
                <div className="flex items-center gap-2"><Mail size={12} />{member.email}</div>
                <div className="flex items-center gap-2"><Phone size={12} />{member.noHP}</div>
                <div className="flex items-center gap-2"><UserCheck size={12} />{member.cabang}</div>
              </div>

              {/* Progress */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">Pencapaian</span>
                  <span className={`font-semibold ${pct >= 100 ? 'text-green-600' : pct >= 75 ? 'text-orange-500' : 'text-red-600'}`}>{pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-2 rounded-full transition-all duration-700 ${pct >= 100 ? 'bg-green-500' : pct >= 75 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>{member.pencapaian} unit</span>
                  <span>Target: {member.target}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => openEdit(member)} className="btn-secondary flex-1 justify-center text-xs py-1.5">Edit</button>
                <button onClick={() => setDeleteId(member.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={14}/></button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">Tidak ada anggota tim ditemukan</div>
      )}

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editData ? 'Edit Anggota Tim' : 'Tambah Anggota Tim'} size="md"
        footer={<><button onClick={() => setShowModal(false)} className="btn-secondary">Batal</button><button onClick={handleSubmit} className="btn-primary">Simpan</button></>}
      >
        <div className="space-y-4">
          <FormRow>
            <Input label="Nama Lengkap *" value={form.nama} onChange={e => setForm({...form, nama: e.target.value})} placeholder="Nama sales" />
            <Input label="Email *" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@honda.co.id" />
          </FormRow>
          <FormRow>
            <Input label="No HP" value={form.noHP} onChange={e => setForm({...form, noHP: e.target.value})} placeholder="081234567890" />
            <Select label="Role" value={form.role} onChange={e => setForm({...form, role: e.target.value})} options={ROLES.map(r => ({ value: r, label: r }))} />
          </FormRow>
          <FormRow>
            <Select label="Cabang" value={form.cabang} onChange={e => setForm({...form, cabang: e.target.value})} options={BRANCHES.map(b => ({ value: b, label: b }))} />
            <Select label="Status" value={form.status} onChange={e => setForm({...form, status: e.target.value})} options={STATUSES} />
          </FormRow>
          <FormRow>
            <Input label="Target Bulanan (unit)" type="number" value={form.target} onChange={e => setForm({...form, target: parseInt(e.target.value) || 0})} min="0" />
            <Input label="Pencapaian Saat Ini" type="number" value={form.pencapaian} onChange={e => setForm({...form, pencapaian: parseInt(e.target.value) || 0})} min="0" />
          </FormRow>
        </div>
      </Modal>

      <ConfirmModal isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => { dispatch({ type: 'DELETE_TEAM_MEMBER', payload: deleteId }); showNotification('Anggota tim dihapus', 'error'); setDeleteId(null); }} title="Hapus Anggota Tim" message="Yakin ingin menghapus anggota tim ini?" confirmLabel="Hapus" confirmClass="btn-danger" />
    </div>
  );
}
